import { createSlice } from '@reduxjs/toolkit';
import { RESET_ALL_REDUCERS, UPDATE_PROFILE } from 'actions';
import { QUICK_VERIFY_PROPERTY } from 'utils/constants';

const INITIAL_STATE = {
    idErrorMsg: undefined,
    verificationConfiguration: undefined,
    sessionTimer: undefined,
    // Timestamp of the last device-link send per channel; drives the resend
    // cool-down and, being persisted, keeps it alive across page navigation.
    resendSentAt: { sms: null, email: null },
    captureQualityFailureCount: 0,
};

const generalSlice = createSlice({
    name: 'general',
    initialState: INITIAL_STATE,
    reducers: {
        // ID error message surfaced on the govt-ID error screens.
        saveIdErrorMessages: (state, action) => {
            state.idErrorMsg = action.payload;
        },
        clearIdErrorMessages: (state) => {
            state.idErrorMsg = undefined;
        },
        // Verification mode: 'SINGLE' | 'AND' | 'OR'.
        setVerificationConfiguration: (state, action) => {
            state.verificationConfiguration = action.payload;
        },
        // Absolute expiry timestamp for the ID capture session timer.
        startSessionTimer: (state, action) => {
            state.sessionTimer = action.payload;
        },
        resetSessionTimer: (state) => {
            state.sessionTimer = undefined;
        },
        // Record when a device link was sent on a channel ('sms' | 'email').
        setResendSentAt: (state, action) => {
            const { channel, time } = action.payload;
            state.resendSentAt[channel] = time;
        },
        incrementCaptureQualityFailureCount: (state) => {
            state.captureQualityFailureCount += 1;
        },
        resetCaptureQualityFailureCount: (state) => {
            state.captureQualityFailureCount = 0;
        },
    },
    extraReducers: (builder) => {
        builder
            // Derive the verification mode from the workflow's id_verifications:
            // quick_verify is excluded; all-mandatory => 'AND', mixed => 'OR',
            // a single enabled method => 'SINGLE'. Sourced from the profile-update
            // action, whose payload lives on `action.data`.
            .addCase(UPDATE_PROFILE, (state, action) => {
                const { workflow } = action?.data ?? {};

                const idVerification = workflow?.id_verifications;
                const enabledVerifications = [];
                const mandatoryVerifications = [];

                for (const key in idVerification) {
                    const check = idVerification[key];

                    if (check.enabled && key !== QUICK_VERIFY_PROPERTY.quick_verify) {
                        enabledVerifications.push(check.verification_type);
                        if (check.mandatory) {
                            mandatoryVerifications.push(check.verification_type);
                        }
                    }
                }

                const allOrAnyMethod =
                    mandatoryVerifications.length === enabledVerifications.length ? 'AND' : 'OR';

                state.verificationConfiguration =
                    enabledVerifications.length === 1 ? 'SINGLE' : allOrAnyMethod;
            })
            /**
             * App-wide reset on session teardown.
             *
             * `sessionTimer` survives it. The bootstrap that dispatches this runs on every page
             * load, not only on a genuinely new session, and the capture budget is wall-clock: a
             * reload part-way through the flow has to carry on counting the same ten minutes, not
             * lose them. Dropping it left the countdown with nothing to render, and only the entry
             * screen restarts it — which a reload deeper in the flow never passes through.
             *
             * A timer inherited by a genuinely new session is handled where it is started: those
             * screens replace one that has already run out.
             *
             * `captureQualityFailureCount` survives it for the same reason: a reload between two
             * quality failures must not lose the first one.
             */
            .addCase(RESET_ALL_REDUCERS, (state) => ({
                ...INITIAL_STATE,
                sessionTimer: state.sessionTimer,
                captureQualityFailureCount: state.captureQualityFailureCount,
            }));
    },
});

export const {
    saveIdErrorMessages,
    clearIdErrorMessages,
    setVerificationConfiguration,
    startSessionTimer,
    resetSessionTimer,
    setResendSentAt,
    incrementCaptureQualityFailureCount,
    resetCaptureQualityFailureCount,
} = generalSlice.actions;

export default generalSlice.reducer;
